import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, SITE } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-black/5 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)]"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className={`max-w-[1440px] mx-auto px-5 md:px-10 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-16" : "h-20 md:h-24"}`}>
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src="/logo-transparent.png"
            alt="Jyoti Graphics USA"
            className={`transition-all duration-500 ${scrolled ? "h-6" : "h-7 md:h-8"} w-auto`}
          />
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-2)]">
            Graphics · USA
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              end={n.href === "/"}
              className={({ isActive }) =>
                `link-underline text-[13px] tracking-wide uppercase font-medium ${isActive ? "text-[color:var(--accent)]" : "text-[color:var(--text)]"}`
              }
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            className="hidden md:inline-flex font-mono text-[11px] tracking-[0.18em] text-[color:var(--text)] link-underline"
            data-testid="header-phone"
          >
            {SITE.phone}
          </a>
          <Link
            to="/quote"
            className="btn-accent hidden md:inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-2.5 text-[12px] tracking-[0.14em] uppercase font-medium"
            data-testid="header-quote-cta"
          >
            Get a Free Quote
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-black/10 bg-white/70"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-white border-t border-black/5"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.href}
                  to={n.href}
                  end={n.href === "/"}
                  className={({ isActive }) =>
                    `font-display text-2xl ${isActive ? "text-[color:var(--accent)]" : "text-[color:var(--text)]"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                className="btn-accent mt-3 inline-flex items-center justify-center gap-2 rounded-full py-3 text-[12px] tracking-[0.14em] uppercase"
                data-testid="mobile-quote-cta"
              >
                Get a Free Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href={SITE.phoneHref} className="font-mono text-sm mt-2">{SITE.phone}</a>
              <a href={SITE.emailHref} className="font-mono text-sm">{SITE.email}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
